import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Flex, Popconfirm,
  Tag, Tooltip,
} from 'antd';
import _, { isNull } from 'lodash';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { UI54Modal, UI54Table } from '@agpl/ui54kit';
import utc from 'dayjs/plugin/utc';
import TableTitleWrapper from '../../components/TableTitleWrapper';
import AntTable from '../../components/AntTable';
import AntDesignSearchBox54origins from '../../54origins/components/AntDesignSearchBox54origins';
import InternalOrExternalTag from './InternalOrExternalTag';

import { getLocale } from '../../locale/selectors';
import { capitalize } from '../../mainUtils';
import { getNameForActor } from '../../54origins/utils54origins';

import useAuthActorsRequests from '../hooks/useAuthActorsRequests';
import BaseButton from '../../_ui/BaseButton/BaseButton';
import BaseTag from '../../_ui/BaseTag/BaseTag';
import ActorInfo from './infoForms/ActorInfo/ActorInfo';
import useURLParams from '../../hooks/useURLParams';
import PermissionsPage from '../../permissions/components/PermissionsPage';
import RobotActorTag from './RobotActorTag';
import { getAllGroups, getDefaultGroupsMap } from '../selectors';

function FormatName({
  popConfirmVisible,
  onConfirm,
  onCancel,
  row,
}) {
  const { t } = useTranslation();
  const allGroups = useSelector(getAllGroups);
  const defaultGroupsMap = useSelector(getDefaultGroupsMap);
  const { actor_type, uinfo, uuid } = row || {};

  const getName = () => {
    const {
      isRoot, isAdmin, email, isRobot, groups,
    } = uinfo;
    const tagName = isRoot ? 'root' : 'default';

    let tagNames = [];

    if (groups && groups.length > 0 && allGroups.length > 0) {
      tagNames = groups
        .map((uuid) => _.get(defaultGroupsMap.get(uuid), 'uinfo.group_name'))
        .filter((item) => ['BAN', 'ADMIN'].includes(item));
    }

    switch (actor_type) {
      case 'service':
        return (
          <Flex gap={5}>
            <Tooltip title={getNameForActor(row)}>
              <p className="text-overflow whitespace-nowrap">
                getNameForActor(row);
              </p>
            </Tooltip>
            <RobotActorTag isRobot={isRobot} />
          </Flex>
        );
      case 'user':
      case 'classic_user':
        return (
          <Flex vertical gap={5}>
            <Flex gap={5}>
              <Tooltip title={getNameForActor(row)}>
                <p className="text-overflow whitespace-nowrap">
                  {getNameForActor(row)}
                </p>
              </Tooltip>
              {(isRoot || isAdmin) && (
                <BaseTag className={`tag-${isRoot ? 'purple' : 'gray'} ml-2`}>
                  {t(`auth.headers.${tagName}`, tagName).toUpperCase()}
                </BaseTag>
              )}
              {tagNames.map((tag) => (
                <BaseTag
                  key={`default_tag_${row.uuid}_${tag}`}
                  className={`tag-${tag === 'BAN' ? 'red' : 'volcano'} ml-2`}
                >
                  {t(`auth.headers.${tag.toLowerCase()}`, tag)}
                </BaseTag>
              ))}
              <RobotActorTag isRobot={isRobot} />
            </Flex>
            {email && (
              <p>
                <span className="font-semibold italic">e-mail: </span>
                <span className="italic">{email}</span>
              </p>
            )}
          </Flex>
        );
      case 'group':
        return (
          <Flex gap={5}>
            <Tooltip title={getNameForActor(row)}>
              <p className="text-overflow whitespace-nowrap">
                {getNameForActor(row)}
              </p>
            </Tooltip>
            {uinfo.isGroupImportant && (
              <BaseTag className="tag-red ml-2">
                Important group
              </BaseTag>
            )}
            <RobotActorTag isRobot={isRobot} />
          </Flex>
        );
      default:
        return 'actor type not found';
    }
  };

  return (
    <Popconfirm
      open={popConfirmVisible === uuid}
      placement="topLeft"
      title="You are trying to add an external user, are you sure?"
      onConfirm={onConfirm}
      onCancel={onCancel}
      okText="Yes"
      cancelText="No"
      color="yellow"
    >
      {getName()}
    </Popconfirm>
  );
}

function ModalListOfActors({
  doNotMakeRequest = [],
  addActorsAfterRequest = [],
  dataForActorsRequest,
  removeActorsAfterRequest = [],
  getSelectedActors,
  listOfActorsActionBtnLabel = 'add to actor',
}) {
  dayjs.extend(utc);
  const { requestGetListOfActorsOnAuth } = useAuthActorsRequests();

  const { t } = useTranslation();

  const locale = useSelector(getLocale) || 'en';

  const initState = {
    tableData: [],
    currentPage: 1,
    pageLimit: 25,
    actorsCount: 0,
    initSearch: false,
    popConfirmVisible: '',
    externalActor: '',
    loading: false,
    orderRules: {
      order_by_column: 'created',
      order_by_rule: 'desc',
    },
  };
  const [state, setState] = useState(initState);

  const [actorInfoModalOpen, setActorInfoModalOpen] = useState(false);

  const { getURLParams, setSearchParams } = useURLParams();

  const {
    tableData,
    currentPage,
    pageLimit,
    actorsCount,
    searchValue,
    initSearch,
    loading,
    orderRules,
    popConfirmVisible,
    externalActor,
  } = state;

  const [modalActorUUID, setModalActorUUID] = useState(null);

  const changeState = (params) => setState((prev) => ({ ...prev, ...params }));

  const pageCountLocalActors = Math.ceil(addActorsAfterRequest.length / pageLimit);

  const changeSort = (param, sortOrder) => {
    changeState({
      orderRules: {
        order_by_column: param,
        order_by_rule: sortOrder === 'ascend' ? 'asc' : 'desc',
      },
    });
  };

  const onChangeActorsPagination = (currentPage) => changeState({ currentPage });

  const onShowSizeChange = (current, size) => changeState({ pageLimit: size });

  const onSearch = (value) => changeState({ searchValue: value });

  const getActorsAndCount = async () => {
    changeState({ loading: true });

    const customOffset = pageLimit * (currentPage - 1 - pageCountLocalActors);

    const finalData = {
      ...orderRules,
      ...dataForActorsRequest,
      limit: pageLimit,
      offset: customOffset,
    };

    if (searchValue) {
      finalData.search_data = {
        value: searchValue,
        ignore_case: true,
        fields:
            {
              base: ['uuid'],
              uinfo: ['first_name', 'last_name', 'group_name', 'service_name', 'email'],
            },
      };
    }

    // console.log('finalData', finalData);

    const actorsRequest = await requestGetListOfActorsOnAuth(finalData, []);

    // console.log('actorsRequest', actorsRequest);

    const { actors = [], total = 0 } = actorsRequest || {};

    let modifiedActors = isNull(actors) ? [] : actors;
    modifiedActors = modifiedActors.filter((item) => !removeActorsAfterRequest.includes(item.uuid));

    changeState({
      actorsCount: total,
      tableData: modifiedActors,
      loading: false,
    });
  };

  const onRow = (record) => ({
    onClick: () => {
      // // onSelectActors(uuid, record)
      // const {
      //   uinfo: {
      //     internal_user: internal = false,
      //   },
      //   uuid,
      // } = record || {};
      //
      // if (internal) {
      //   getSelectedActors([record]);
      // } else {
      //   changeState({
      //     popConfirmVisible: uuid,
      //     externalActor: record,
      //   });
      // }

      setSearchParams({
        tab: 'permDigest',
      });

      setModalActorUUID(record.uuid);

      setActorInfoModalOpen(true);
    },
  });

  const onConfirm = () => {
    getSelectedActors([externalActor]);
    changeState({
      popConfirmVisible: '',
      externalActor: '',
    });
  };

  const onCancel = (e) => {
    e.stopPropagation();

    changeState({
      popConfirmVisible: '',
      externalActor: '',
    });
  };

  const formatActorType = (cell) => (
    <BaseTag className={`tag-${cell}`}>
      {capitalize(t(`auth.headers.${cell}`, cell))}
    </BaseTag>
  );

  const formatActions = (cell, row) => (
    <div>
      <BaseButton
        className="button-access-outlined px-2"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          getSelectedActors([row]);
        }}
      >
        {listOfActorsActionBtnLabel}
      </BaseButton>
    </div>
  );

  const columns = useMemo(() => [
    {
      dataIndex: 'uuid',
      // colSpan: 1,
      key: 'actions',
      className: 'p-2 pr-4',
      title: '',
      render: formatActions,
    },
    {
      dataIndex: 'name',
      key: 'name',
      className: 'p-2 max-width-200px',
      title: (
        <AntDesignSearchBox54origins
          onSearch={onSearch}
          placeholder="Enter actor name or UUID or e-mail"
        />
      ),
      sorter: (a, b, sortOrder) => changeSort('title', sortOrder),
      render: (cell, row) => (
        <FormatName
          cell={cell}
          row={row}
          popConfirmVisible={popConfirmVisible}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      ),
      // shouldCellUpdate: (record, prevRecord) => record?.uuid === popConfirmVisible,
    },
    {
      dataIndex: 'actor_type',
      key: 'actor_type',
      title: (
        <TableTitleWrapper minWidth={40}>
          {capitalize(t('auth.headers.type', 'type'))}
        </TableTitleWrapper>
      ),
      sorter: (a, b, sortOrder) => changeSort('actor_type', sortOrder),
      className: 'p-2',
      align: 'left',
      render: formatActorType,
    },
    {
      dataIndex: ['uinfo', 'internal_user'],
      key: 'internal_user',
      title: (
        <TableTitleWrapper minWidth={40}>
          {capitalize(t('type', 'subtype'))}
        </TableTitleWrapper>
      ),
      sorter: (a, b, sortOrder) => changeSort('internal_user', sortOrder),
      className: 'p-2',
      align: 'left',
      render: (cell) => <InternalOrExternalTag internal={cell} />,
    },
    {
      dataIndex: 'created',
      key: 'created',
      title: (
        <TableTitleWrapper minWidth={120}>
          {capitalize(t('auth.headers.created', 'created'))}
        </TableTitleWrapper>
      ),
      className: 'p-2',
      textWrap: 'word-break',
      sorter: (a, b, sortOrder) => changeSort('created', sortOrder),
      render: (cell) => capitalize(dayjs.utc(cell)
        .local()
        .locale(locale)
        .format('MMM D, h:mm A')),
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: ['uinfo', 'date_updated'],
      key: 'modify',
      title: (
        <TableTitleWrapper minWidth={120}>
          {capitalize(t('auth.headers.modify', 'modify'))}
        </TableTitleWrapper>
      ),
      className: 'p-2',
      textWrap: 'word-break',
      // sorter: (a, b, sortOrder) => changeSort('created', sortOrder),
      render: (cell) => cell && capitalize(dayjs.utc(cell)
        .local()
        .locale(locale)
        .format('MMM D, h:mm A')),
      // defaultSortOrder: 'descend',
    },
  ], [tableData]);

  const rowSelection = {
    columnWidth: 40,
    selectedRowKeys: [],
  };

  const calculateTotal = () => actorsCount + (pageCountLocalActors * pageLimit);

  const getActorsIfEmpty = async () => {
    onChangeActorsPagination(Math.abs(currentPage - 1));
    await getActorsAndCount();
  };

  useEffect(() => {
    if (!doNotMakeRequest.includes('getActors')
    // || dataForActorsRequest.uuid.length === 0
    ) {
      getActorsAndCount();

      if (searchValue && !initSearch) {
        // console.log('FIRST SEARCH')
        changeState({
          currentPage: 1,
          initSearch: true,
        });
      } else if (!searchValue) {
        // console.log('EMPTY SEARCH')
        changeState({ initSearch: false });
      }
    }
  }, [
    currentPage,
    pageLimit,
    searchValue,
    JSON.stringify(orderRules),
    JSON.stringify(dataForActorsRequest),
  ]);

  useEffect(() => {
    if (tableData.length === 0 && currentPage !== 1) {
      getActorsIfEmpty();
    }
  }, [tableData.length]);

  // console.log('tableData', tableData);

  return (
    <div>
      <div className="overflow-hidden">
        <UI54Table
          columns={columns}
          dataSource={tableData}
          disablePagination={false}
          loading={loading}
          onRow={onRow}
          rowSelection={rowSelection}
          pagination={{
            onChange: onChangeActorsPagination,
            current: currentPage,
            onShowSizeChange,
            pageSize: pageLimit,
            paginationSize: 'default',
            total: calculateTotal(),
            pageSizeOptions: [10, 25, 50],
          }}
          tableLayout="auto"
        />
      </div>

      <UI54Modal
        open={actorInfoModalOpen}
        onClose={() => setActorInfoModalOpen(false)}
        onCancel={() => setActorInfoModalOpen(false)}
        width="1600px"
        footer={null}
      >
        <PermissionsPage
          modalActorUUID={modalActorUUID}
          hideElements={['actorsPermsOnServiceBtn', 'closeBtn', 'editBtn']}
          modalView
        />
      </UI54Modal>

      {/* <AntTable */}
      {/*  columns={columns} */}
      {/*  current={currentPage} */}
      {/*  data={tableData} */}
      {/*  disablePagination={false} */}
      {/*  loading={loading} */}
      {/*  onChangePagination={onChangeActorsPagination} */}
      {/*  onRow={onRow} */}
      {/*  onShowSizeChange={onShowSizeChange} */}
      {/*  paginationPageSize={pageLimit} */}
      {/*  paginationSize="default" */}
      {/*  rowSelection={rowSelection} */}
      {/*  total={calculateTotal()} */}
      {/* /> */}
    </div>
  );
}

export default ModalListOfActors;

ModalListOfActors.propTypes = {
  addActorsAfterRequest: PropTypes.array,
  dataForActorsRequest: PropTypes.any,
  doNotMakeRequest: PropTypes.array,
  getSelectedActors: PropTypes.func,
  listOfActorsActionBtnLabel: PropTypes.string,
  removeActorsAfterRequest: PropTypes.array,
};

FormatName.propTypes = {
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  popConfirmVisible: PropTypes.bool,
  row: PropTypes.any,
};
